import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Accessibility, 
  Type, 
  Eye, 
  Palette, 
  Moon, 
  Sun, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw,
  Save,
  Settings,
  X,
  Plus,
  Minus,
  Check
} from 'lucide-react';

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('display');
  const [isMobile, setIsMobile] = useState(false);
  
  // הגדרות תצוגה
  const [fontSize, setFontSize] = useState(() => 
    parseInt(localStorage.getItem('accessibility-font-size')) || 16
  );
  const [zoom, setZoom] = useState(() => 
    parseInt(localStorage.getItem('accessibility-zoom')) || 100
  );
  const [highContrast, setHighContrast] = useState(() => 
    localStorage.getItem('accessibility-high-contrast') === 'true'
  );
  const [darkMode, setDarkMode] = useState(() => 
    localStorage.getItem('accessibility-dark-mode') === 'true'
  );
  const [reduceMotion, setReduceMotion] = useState(() => 
    localStorage.getItem('accessibility-reduce-motion') === 'true'
  );
  const [highlightLinks, setHighlightLinks] = useState(() => 
    localStorage.getItem('accessibility-highlight-links') === 'true'
  );
  const [largerCursor, setLargerCursor] = useState(() => 
    localStorage.getItem('accessibility-larger-cursor') === 'true'
  );

  // זיהוי מכשיר נייד
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // שמירת הגדרות
  const saveSettings = () => {
    localStorage.setItem('accessibility-font-size', fontSize);
    localStorage.setItem('accessibility-zoom', zoom);
    localStorage.setItem('accessibility-high-contrast', highContrast);
    localStorage.setItem('accessibility-dark-mode', darkMode);
    localStorage.setItem('accessibility-reduce-motion', reduceMotion);
    localStorage.setItem('accessibility-highlight-links', highlightLinks);
    localStorage.setItem('accessibility-larger-cursor', largerCursor);
  };

  // החלת הגדרות
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
    document.body.style.zoom = `${zoom}%`;
    
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    if (reduceMotion) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
    
    if (highlightLinks) {
      document.body.classList.add('highlight-links');
    } else {
      document.body.classList.remove('highlight-links');
    }
    
    if (largerCursor) {
      document.body.classList.add('larger-cursor');
    } else {
      document.body.classList.remove('larger-cursor');
    }
  }, [fontSize, zoom, highContrast, darkMode, reduceMotion, highlightLinks, largerCursor]);

  const tabs = [
    { id: 'display', label: 'תצוגה', icon: Eye },
    { id: 'text', label: 'טקסט', icon: Type },
    { id: 'colors', label: 'צבעים', icon: Palette },
    { id: 'navigation', label: 'ניווט', icon: Settings }
  ];

  const resetAllSettings = () => {
    setFontSize(16);
    setZoom(100);
    setHighContrast(false);
    setDarkMode(false);
    setReduceMotion(false);
    setHighlightLinks(false);
    setLargerCursor(false);
    localStorage.clear();
  };

  return (
    <>
      {/* כפתור נגישות מעופף */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-36 left-3 bg-black z-50 bg-gradient-to-r from-brand-primary to-[#ac609c] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 group"
        aria-label="פתח תפריט נגישות"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Accessibility className="w-5 h-5 sm:w-6 bg-black sm:h-6 group-hover:rotate-12 transition-transform duration-300" />
        {!isOpen && (
          <motion.div
            className="absolute -top-7 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs py-1 px-2 rounded whitespace-nowrap"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            נגישות
          </motion.div>
        )}
      </motion.button>

      {/* תפריט נגישות */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* רקע */}
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            
            {/* תפריט */}
            <motion.div
              className={`fixed z-50 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl shadow-2xl border border-gray-700 overflow-hidden ${
                isMobile 
                  ? 'inset-x-4 inset-y-8 max-h-[85vh]' 
                  : 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-2xl max-h-[90vh]'
              }`}
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20 }}
            >
              {/* כותרת */}
              <div className="bg-gradient-to-r from-brand-primary to-[#ac609c] p-4 sm:p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Accessibility className="w-6 h-6 sm:w-8 sm:h-8" />
                    <div>
                      <h2 className="text-lg sm:text-2xl font-bold">מרכז נגישות</h2>
                      <p className="text-white/90 text-xs sm:text-sm">התאמת האתר לצרכים שלך</p>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </motion.button>
                </div>
              </div>

              {/* טאבים - רספונסיבי */}
              <div className={`bg-gray-800 border-b border-gray-700 ${isMobile ? 'overflow-x-auto' : 'flex'}`}>
                <div className={`${isMobile ? 'flex min-w-max' : 'flex w-full'}`}>
                  {tabs.map((tab) => (
                    <motion.button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap transition-all duration-300 text-sm sm:text-base ${
                        activeTab === tab.id
                          ? 'bg-brand-primary text-white border-b-2 border-brand-primary'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      } ${isMobile ? 'min-w-0 flex-shrink-0' : 'flex-1'}`}
                      whileHover={{ y: -2 }}
                    >
                      <tab.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className={isMobile ? 'text-xs' : ''}>{tab.label}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* תוכן */}
              <div className={`p-3 sm:p-6 overflow-y-auto ${isMobile ? 'max-h-96' : 'max-h-96'}`}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* תצוגה */}
                    {activeTab === 'display' && (
                      <div className="space-y-4 sm:space-y-6">
                        {/* זום */}
                        <div className="bg-gray-800/50 rounded-2xl p-3 sm:p-4">
                          <h3 className="text-white font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                            <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />
                            רמת זום
                          </h3>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <button
                              onClick={() => setZoom(Math.max(50, zoom - 10))}
                              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                            >
                              <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </button>
                            <div className="flex-1 bg-gray-700 rounded-lg p-2 sm:p-3 text-center text-white font-medium text-sm sm:text-base">
                              {zoom}%
                            </div>
                            <button
                              onClick={() => setZoom(Math.min(200, zoom + 10))}
                              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                            >
                              <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </button>
                          </div>
                        </div>

                        {/* הגדרות תצוגה */}
                        <div className="grid grid-cols-1 gap-3 sm:gap-4">
                          <ToggleCard
                            title="ניגודיות גבוהה"
                            description="שיפור קריאות הטקסט"
                            checked={highContrast}
                            onChange={setHighContrast}
                            icon={<Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                            isMobile={isMobile}
                          />
                          <ToggleCard
                            title="עצור אנימציות"
                            description="הפחתת תנועות במסך"
                            checked={reduceMotion}
                            onChange={setReduceMotion}
                            icon={<RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />}
                            isMobile={isMobile}
                          />
                        </div>
                      </div>
                    )}

                    {/* טקסט */}
                    {activeTab === 'text' && (
                      <div className="space-y-4 sm:space-y-6">
                        {/* גודל טקסט */}
                        <div className="bg-gray-800/50 rounded-2xl p-3 sm:p-4">
                          <h3 className="text-white font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                            <Type className="w-4 h-4 sm:w-5 sm:h-5" />
                            גודל הטקסט
                          </h3>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <button
                              onClick={() => setFontSize(Math.max(12, fontSize - 2))}
                              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                            >
                              <Minus className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </button>
                            <div className="flex-1 bg-gray-700 rounded-lg p-2 sm:p-3 text-center text-white font-medium text-sm sm:text-base">
                              {fontSize}px
                            </div>
                            <button
                              onClick={() => setFontSize(Math.min(32, fontSize + 2))}
                              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                            >
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </button>
                          </div>
                          <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-gray-700 rounded-lg">
                            <p className="text-white text-center text-xs sm:text-sm">טקסט לדוגמה - בדיקת הגודל</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* צבעים */}
                    {activeTab === 'colors' && (
                      <div className="space-y-3 sm:space-y-4">
                        <ToggleCard
                          title="מצב לילה"
                          description="צבעים כהים לעיניים רגישות"
                          checked={darkMode}
                          onChange={setDarkMode}
                          icon={darkMode ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
                          isMobile={isMobile}
                        />
                        <ToggleCard
                          title="הדגשת קישורים"
                          description="הדגשה בולטת יותר של קישורים"
                          checked={highlightLinks}
                          onChange={setHighlightLinks}
                          icon={<Palette className="w-4 h-4 sm:w-5 sm:h-5" />}
                          isMobile={isMobile}
                        />
                      </div>
                    )}

                    {/* ניווט */}
                    {activeTab === 'navigation' && (
                      <div className="space-y-4 sm:space-y-6">
                        <ToggleCard
                          title="סמן עכבר גדול"
                          description="סמן עכבר בגודל מוגדל"
                          checked={largerCursor}
                          onChange={setLargerCursor}
                          icon={<Settings className="w-4 h-4 sm:w-5 sm:h-5" />}
                          isMobile={isMobile}
                        />
                        
                        {/* קיצורי דרך */}
                        <div className="bg-gray-800/50 rounded-2xl p-3 sm:p-4">
                          <h3 className="text-white font-semibold mb-2 sm:mb-3 text-sm sm:text-base">קיצורי מקלדת</h3>
                          <div className="space-y-2 text-xs sm:text-sm text-gray-300">
                            <div className="flex justify-between items-center">
                              <span>ניווט בין אלמנטים</span>
                              <kbd className="px-2 py-1 bg-gray-700 rounded text-xs">Tab</kbd>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>הפעלת כפתורים</span>
                              <kbd className="px-2 py-1 bg-gray-700 rounded text-xs">Enter</kbd>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>סגירת חלונות</span>
                              <kbd className="px-2 py-1 bg-gray-700 rounded text-xs">Esc</kbd>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>הגדלת טקסט</span>
                              <kbd className="px-2 py-1 bg-gray-700 rounded text-xs">Ctrl +</kbd>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* פעולות */}
              <div className="bg-gray-800 p-3 sm:p-4 flex gap-2 sm:gap-3">
                <motion.button
                  onClick={saveSettings}
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm sm:text-base flex-1 justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>שמור הגדרות</span>
                </motion.button>
                
                <motion.button
                  onClick={resetAllSettings}
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm sm:text-base"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className={isMobile ? 'hidden' : ''}>איפוס</span>
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// קומפוננט עזר לכרטיס הפעלה/כיבוי - מותאם למובייל
const ToggleCard = ({ title, description, checked, onChange, icon, isMobile }) => (
  <motion.div
    className="bg-gray-800/50 rounded-2xl p-3 sm:p-4 cursor-pointer"
    onClick={() => onChange(!checked)}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex items-start gap-2 sm:gap-3">
      <div className={`p-2 rounded-lg flex-shrink-0 ${checked ? 'bg-green-600' : 'bg-gray-700'}`}>
        {checked ? <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium text-sm sm:text-base">{title}</h4>
        <p className="text-gray-400 text-xs sm:text-sm mt-1 leading-tight">{description}</p>
      </div>
      <div className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-green-600' : 'bg-gray-600'}`}>
        <motion.div
          className="w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full m-0.5"
          animate={{ x: checked ? (isMobile ? 20 : 24) : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      </div>
    </div>
  </motion.div>
);

export default AccessibilityMenu; 